/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   ใบขอเบิกเพื่อเตรียมการผลิตประจำวัน (ห้องตัดแต่ง)

   1 Production Order → 1 ฟอร์มต่อสินค้า 1 รายการ
   แสดงเฉพาะวัตถุดิบที่ต้องตัดแต่ง (%yield ไม่ถึง 100)
   ถ้าไม่มีรายการที่ยืลด์ต่ำกว่า 100 จะแสดงวัตถุดิบทั้งหมดแทน
   ช่องยกมา/ยกไป/สรุปเบิกใช้จริง/วันที่รับเข้า เว้นว่างให้เขียนมือ
============================================================ */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import PdDocMeta from "../pd-common/PdDocMeta";
import { Modal, Button } from "antd";
import { PiPrinterFill } from "react-icons/pi";

import logo from "../../../assets/images/logo_nsf.png";
import ProductionOrderService from "../../../service/ERP/ProductionOrder.service";
import "./pd-trim-room.css";

const pdService = ProductionOrderService();
const PAGE_STYLE_ID = "pd-trim-print-page";

/* จำนวนแถวขั้นต่ำ เติมแถวว่างให้ฟอร์มเต็มเหมือนกระดาษเดิม */
const MIN_ROWS = 8;

const wnum = (v, digits = 3) =>
  Number(v || 0) > 0
    ? Number(v).toLocaleString("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    : "";

export default function PdTrimRoomPrintPreview() {
  const { code } = useParams();
  const [header, setHeader] = useState(null);
  const [forms, setForms] = useState([]);

  const handlePrint = () => window.print();

  useEffect(() => {
    let style = document.getElementById(PAGE_STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = PAGE_STYLE_ID;
      style.media = "print";
      style.innerHTML = "@page { size: A4 portrait; margin: 8mm; }";
      document.head.appendChild(style);
    }
    return () => {
      const s = document.getElementById(PAGE_STYLE_ID);
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    pdService
      .trimRoom(code)
      .then((res) => {
        setHeader(res.data?.data?.header || null);
        setForms(res.data?.data?.forms || []);
      })
      .catch((err) => {
        Modal.error({
          title: "Error",
          content: err.response?.data?.message || "โหลดข้อมูลไม่สำเร็จ",
          onOk: () => window.close(),
        });
      });
  }, [code]);

  return (
    <div className="page-show" id="pd-trim">
      <div className="doc-wrapper">
        <div className="print-bar no-print">
          <Button
            className="bn-center bg-blue-400"
            onClick={handlePrint}
            icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}
          >
            PRINT
          </Button>
        </div>

        {forms.map((f, fi) => {
          /* RM / WI ทั้งหมด (คัดกลุ่มมาจาก API แล้ว) */
          const rows = f.materials || [];
          const blank = Math.max(MIN_ROWS - rows.length, 0);

          return (
            <section className="print-page" key={`${f.pg_code}-${fi}`}>
              <div className="tr-head">
                <img src={logo} alt="" className="tr-logo" />
                <div className="tr-title">
                  <p className="tr-company">บริษัทไนน์สตาร์ฟู้ด จำกัด</p>
                  <p className="tr-doc">ใบขอเบิกวัตถุดิบ</p>
                </div>
              </div>

              <PdDocMeta form={f} date={dayjs().format("DD/MM/YYYY")} />

              <table className="tr-table">
                <colgroup>
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "22%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "9%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan={3}>
                      รหัสวัตถุดิบ
                      <br />/ ส่วนผสม
                    </th>
                    <th rowSpan={3}>รายการวัตถุดิบ</th>
                    <th colSpan={2}>จำนวน</th>
                    <th rowSpan={2}>%yield</th>
                    <th colSpan={2}>วัตถุดิบเหลือตัดแต่ง</th>
                    <th rowSpan={2}>
                      สรุปเบิก
                      <br />
                      ใช้จริง
                      <br />
                      วัตถุดิบสด
                    </th>
                    <th rowSpan={3}>วันที่รับเข้า/Lot.</th>
                  </tr>
                  <tr>
                    <th>
                      <u>{f.batch_count || 0}</u> ชุด
                    </th>
                    <th>น้ำหนักรวม</th>
                    <th>ยกมา</th>
                    <th>ยกไป</th>
                  </tr>
                  <tr>
                    <th>น้ำหนัก/ชุด</th>
                    <th>kg.</th>
                    <th>%</th>
                    <th>kg.</th>
                    <th>kg.</th>
                    <th>kg.</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m, i) => (
                    <tr key={`${m.mat_code}-${i}`}>
                      <td className="c">{m.mat_code}</td>
                      <td>{m.mat_name}</td>
                      <td className="r">{wnum(m.per_set)}</td>
                      <td className="r">{wnum(m.total)}</td>
                      <td className="c">
                        {m.yield === null || m.yield === undefined
                          ? ""
                          : `${Number(m.yield)}%`}
                      </td>
                      <td />
                      <td />
                      <td />
                      <td />
                    </tr>
                  ))}
                  {Array.from({ length: blank }).map((_, i) => (
                    <tr className="empty" key={`b-${i}`}>
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* footer เหมือนห้องสารส่วนผสม : ลายเซ็น + เลขหน้า ชิดล่างกระดาษ */}
              <div className="tr-foot">
                <div className="tr-sign">
                  <span>ผู้จ่าย_______________</span>
                  <span>ผู้ตรวจสอบ_______________</span>
                </div>
                <div className="tr-pageno">
                  <span>{code}</span>
                  <span>หน้า {fi + 1} / {forms.length}</span>
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
